import React, { useEffect, useState } from "react";

interface CategoryAssignmentProps {
  categories: { [key: string]: number }; // Category name -> index mapping
  indexWaterType: number;
  bucketValues: {
    "fgw": [string[], string[], string[]],
    "bgw": [string[], string[], string[]],
    "fsw": [string[], string[], string[]]
  };
  setBucketValues: React.Dispatch<React.SetStateAction<{
    "fgw": [string[], string[], string[]],
    "bgw": [string[], string[], string[]],
    "fsw": [string[], string[], string[]]
  }>>;
}

const waterTypeKeys = ["fsw", "bgw", "fgw"] as const;
type WaterTypeKey = typeof waterTypeKeys[number];

const CategoryAssignment: React.FC<CategoryAssignmentProps> = ({
  categories,
  indexWaterType,
  bucketValues,
  setBucketValues
}) => {
  // Get appropriate water type key
  const waterType: WaterTypeKey = waterTypeKeys[indexWaterType];

  // All available categories from props
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  
  // Selected categories in each bucket
  const [highCategories, setHighCategories] = useState<string[]>([]);
  const [mediumCategories, setMediumCategories] = useState<string[]>([]);
  const [lowCategories, setLowCategories] = useState<string[]>([]);
  
  // Selected items in the available list
  const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
  const [selectedHigh, setSelectedHigh] = useState<string[]>([]);
  const [selectedMedium, setSelectedMedium] = useState<string[]>([]);
  const [selectedLow, setSelectedLow] = useState<string[]>([]);

  // Initialize categories on component mount or when categories/bucketValues change
  useEffect(() => {
    // Initialize from props only once or when explicitly needed
    if (availableCategories.length === 0 && highCategories.length === 0 && 
        mediumCategories.length === 0 && lowCategories.length === 0) {
      
      // Get all categories
      const allCats = Object.keys(categories);
      
      // Get currently assigned categories from bucketValues
      const highCats = [...bucketValues[waterType][0]];
      const mediumCats = [...bucketValues[waterType][1]];
      const lowCats = [...bucketValues[waterType][2]];
      
      // Set available categories (those not assigned to any bucket)
      const availableCats = allCats.filter(cat => 
        !highCats.includes(cat) && 
        !mediumCats.includes(cat) && 
        !lowCats.includes(cat)
      );
      
      setAvailableCategories(availableCats);
      setHighCategories(highCats);
      setMediumCategories(mediumCats);
      setLowCategories(lowCats);
    }
  }, [categories, bucketValues, waterType]);

  // Update bucketValues when categories are explicitly moved between columns
  const updateBucketValues = () => {
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [
      [...highCategories],
      [...mediumCategories],
      [...lowCategories]
    ];
    setBucketValues(newBucketValues);
  };

  // Handle selection in available categories list
  const handleAvailableSelect = (category: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedAvailable.includes(category)) {
      setSelectedAvailable(selectedAvailable.filter(c => c !== category));
    } else {
      setSelectedAvailable([...selectedAvailable, category]);
    }
  };

  // Handle selection in high categories list
  const handleHighSelect = (category: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedHigh.includes(category)) {
      setSelectedHigh(selectedHigh.filter(c => c !== category));
    } else {
      setSelectedHigh([...selectedHigh, category]);
    }
  };

  // Handle selection in medium categories list
  const handleMediumSelect = (category: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedMedium.includes(category)) {
      setSelectedMedium(selectedMedium.filter(c => c !== category));
    } else {
      setSelectedMedium([...selectedMedium, category]);
    }
  };

  // Handle selection in low categories list
  const handleLowSelect = (category: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedLow.includes(category)) {
      setSelectedLow(selectedLow.filter(c => c !== category));
    } else {
      setSelectedLow([...selectedLow, category]);
    }
  };

  // Move selected available categories to high
  const moveToHigh = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedAvailable.length === 0) return;

    const new_high_categories = [...highCategories, ...selectedAvailable];
    
    setHighCategories(new_high_categories);
    setAvailableCategories(availableCategories.filter(c => !selectedAvailable.includes(c)));
    setSelectedAvailable([]);
    
    // Update parent state
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [
      [...new_high_categories],
      [...mediumCategories],
      [...lowCategories]
    ];
    setBucketValues(newBucketValues);
  };

  // Move selected available categories to medium
  const moveToMedium = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedAvailable.length === 0) return;

    const new_meduim_categories = [...mediumCategories, ...selectedAvailable];
    
    setMediumCategories(new_meduim_categories);
    setAvailableCategories(availableCategories.filter(c => !selectedAvailable.includes(c)));
    setSelectedAvailable([]);
    
    // Update parent state
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [
      [...highCategories],
      [...new_meduim_categories],
      [...lowCategories]
    ];
    setBucketValues(newBucketValues);
  };

  // Move selected available categories to low
  const moveToLow = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedAvailable.length === 0) return;

    const new_low_categories = [...lowCategories, ...selectedAvailable];
    
    setLowCategories(new_low_categories);
    setAvailableCategories(availableCategories.filter(c => !selectedAvailable.includes(c)));
    setSelectedAvailable([]);
    
    // Update parent state
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [
      [...highCategories],
      [...mediumCategories],
      [...new_low_categories]
    ];
    setBucketValues(newBucketValues);
  };

  // Move high categories back to available
  const moveFromHigh = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedHigh.length === 0) return;

    const new_high_categories = highCategories.filter(c => !selectedHigh.includes(c));
    
    setAvailableCategories([...availableCategories, ...selectedHigh]);
    setHighCategories(new_high_categories);
    setSelectedHigh([]);
    
    // Update parent state
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [
      [...new_high_categories],
      [...mediumCategories],
      [...lowCategories]
    ];
    setBucketValues(newBucketValues);
  };

  // Move medium categories back to available
  const moveFromMedium = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedMedium.length === 0) return;

    const new_meduim_categories = mediumCategories.filter(c => !selectedMedium.includes(c));
    
    setAvailableCategories([...availableCategories, ...selectedMedium]);
    setMediumCategories(new_meduim_categories);
    setSelectedMedium([]);
    
    // Update parent state
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [
      [...highCategories],
      [...new_meduim_categories],
      [...lowCategories]
    ];
    setBucketValues(newBucketValues);
  };

  // Move low categories back to available
  const moveFromLow = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    if (selectedLow.length === 0) return;

    const new_low_categories = lowCategories.filter(c => !selectedLow.includes(c));
    
    setAvailableCategories([...availableCategories, ...selectedLow]);
    setLowCategories(new_low_categories);
    setSelectedLow([]);
    
    // Update parent state
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [
      [...highCategories],
      [...mediumCategories],
      [...new_low_categories]
    ];
    setBucketValues(newBucketValues);
  };

  // Reset all categories to available
  const handleReset = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    
    const allCategories = [
      ...availableCategories,
      ...highCategories,
      ...mediumCategories,
      ...lowCategories
    ];
    
    setAvailableCategories(allCategories);
    setHighCategories([]);
    setMediumCategories([]);
    setLowCategories([]);
    setSelectedAvailable([]);
    setSelectedHigh([]);
    setSelectedMedium([]);
    setSelectedLow([]);
    
    // Update parent state
    const newBucketValues = { ...bucketValues };
    newBucketValues[waterType] = [[], [], []];
    setBucketValues(newBucketValues);
  };

  // Common button style for right/left buttons
  const arrowButtonStyle = (disabled: boolean) => ({
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    fontSize: '14px'
  });

  // Common style for category items
  const categoryItemStyle = (isSelected: boolean) => ({
    padding: '4px 8px',
    cursor: 'pointer',
    backgroundColor: isSelected ? '#e5f2ff' : 'white',
    borderRadius: '4px',
    marginBottom: '5px',
    marginRight: '5px',
    fontSize: '13px',
    border: '1px solid #e2e8f0',
    display: 'inline-block'
  });


  return (
    <div style={{ 
      width: '100%',
      maxWidth: '100%',
      marginTop: '15px',
    }}
    onClick={(e) => e.stopPropagation()} // Prevent propagation at container level
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '10px',
      }}>
        <div style={{ width: '100%' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Categorization</h4>
            <button 
              onClick={handleReset}
              style={{
                background: 'none',
                border: 'none',
                color: '#1976d2',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0'
              }}
            >
              Reset
            </button>
          </div>
  
          {/* Main container with columns and gap */}
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            gap: '25px', // Create a visible gap between columns
            position: 'relative'
          }}>
            {/* Left column - Available Categories */}
            <div style={{
              width: '45%',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              padding: '8px',
              backgroundColor: 'white',
              minHeight: '200px',
              color:"#1976d2"
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>
                Available Categories
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '5px'
              }}>
                {availableCategories.map(category => (
                  <div 
                    key={category}
                    onClick={(e) => handleAvailableSelect(category, e)}
                    style={categoryItemStyle(selectedAvailable.includes(category))}
                  >
                    {category}
                  </div>
                ))}
              </div>
            </div>
  
            {/* Center buttons column */}
            <div style={{
              width: '10%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly', // Evenly distribute buttons
              alignItems: 'center',
              padding: '30px 0'
            }}>
              {/* High category buttons */}
              <div style={{
                display: 'flex',
                gap: '5px',
                marginBottom: '20px'
              }}>
                <button
                  onClick={moveFromHigh}
                  disabled={selectedHigh.length === 0}
                  style={arrowButtonStyle(selectedHigh.length === 0)}
                >
                  &lt;
                </button>
                <button
                  onClick={moveToHigh}
                  disabled={selectedAvailable.length === 0}
                  style={arrowButtonStyle(selectedAvailable.length === 0)}
                >
                  &gt;
                </button>
              </div>
  
              {/* Medium category buttons */}
              <div style={{
                display: 'flex',
                gap: '5px',
                marginBottom: '20px'
              }}>
                <button
                  onClick={moveFromMedium}
                  disabled={selectedMedium.length === 0}
                  style={arrowButtonStyle(selectedMedium.length === 0)}
                >
                  &lt;
                </button>
                <button
                  onClick={moveToMedium}
                  disabled={selectedAvailable.length === 0}
                  style={arrowButtonStyle(selectedAvailable.length === 0)}
                >
                  &gt;
                </button>
              </div>
  
              {/* Low category buttons */}
              <div style={{
                display: 'flex',
                gap: '5px'
              }}>
                <button
                  onClick={moveFromLow}
                  disabled={selectedLow.length === 0}
                  style={arrowButtonStyle(selectedLow.length === 0)}
                >
                  &lt;
                </button>
                <button
                  onClick={moveToLow}
                  disabled={selectedAvailable.length === 0}
                  style={arrowButtonStyle(selectedAvailable.length === 0)}
                >
                  &gt;
                </button>
              </div>
            </div>
  
            {/* Right column - Categories */}
            <div style={{
              width: '45%',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {/* High Category */}
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: 'white',
                minHeight: '50px',
                color:"#1976d2"
              }}>
                <div style={{ 
                  fontWeight: 600, 
                  marginBottom: '8px', 
                  fontSize: '14px'
                }}>
                  <span>High</span>
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '5px'
                }}>
                  {highCategories.map(category => (
                    <div 
                      key={category}
                      onClick={(e) => handleHighSelect(category, e)}
                      style={categoryItemStyle(selectedHigh.includes(category))}
                    >
                      {category}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Medium Category */}
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: 'white',
                minHeight: '50px',
                color:"#1976d2"
              }}>
                <div style={{ 
                  fontWeight: 600, 
                  marginBottom: '8px', 
                  fontSize: '14px'
                }}>
                  <span>Medium</span>
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '5px'
                }}>
                  {mediumCategories.map(category => (
                    <div 
                      key={category}
                      onClick={(e) => handleMediumSelect(category, e)}
                      style={categoryItemStyle(selectedMedium.includes(category))}
                    >
                      {category}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Low Category */}
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: 'white',
                minHeight: '50px',
                color:"#1976d2"
              }}>
                <div style={{ 
                  fontWeight: 600, 
                  marginBottom: '8px', 
                  fontSize: '14px'
                }}>
                  <span>Low</span>
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '5px'
                }}>
                  {lowCategories.map(category => (
                    <div 
                      key={category}
                      onClick={(e) => handleLowSelect(category, e)}
                      style={categoryItemStyle(selectedLow.includes(category))}
                    >
                      {category}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};

export default CategoryAssignment;